import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, pluck, shareReplay } from 'rxjs/operators';

export interface LoaderState {
  visible: boolean;
  totalLoading: number;
  message: string | null;
}

@Injectable()
export class LoaderService {
  private readonly loaderStateSubject = new BehaviorSubject<LoaderState>({
    visible: false,
    totalLoading: 0,
    message: null,
  });

  public readonly message$ = this.loaderStateSubject.pipe(
    pluck('message'),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public readonly visible$ = this.loaderStateSubject.pipe(
    map(({ totalLoading, visible }) => {
      return visible || totalLoading > 0;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public setMessage(message: string) {
    this.updateState({ message });
  }

  public show(message?: string) {
    if (this.loaderStateSubject.value.visible) {
      return;
    }

    this.updateState({ visible: true, message });
  }

  public hide() {
    if (!this.loaderStateSubject.value.visible) {
      return;
    }

    this.updateState({ visible: false });
  }

  private updateState(state: Partial<LoaderState>) {
    this.loaderStateSubject.next({
      ...this.loaderStateSubject.value,
      ...state,
    });
  }

  public load(obs$: Observable<any>, message?: string | null) {
    const add = (value: number) =>
      this.updateState({ totalLoading: this.loaderStateSubject.value.totalLoading + value });

    if (message !== undefined) {
      this.updateState({ message });
    }

    add(1);

    obs$.subscribe({
      complete: () => add(-1),
      error: () => add(-1),
    });
  }
}
